import type { PrV2StackPullRequest } from '@/lib/pr-v2/types';
import type { LucideIcon } from 'lucide-react';
import {
  Check,
  Circle,
  CircleDashed,
  GitMerge,
  GitPullRequestClosed,
  Loader2,
  X,
} from 'lucide-react';

/** The one thing the stack menu says about a PR: the most urgent fact, or nothing. */
export type PrV2StackStatus =
  | { kind: 'merged' }
  | { kind: 'closed' }
  | { kind: 'draft' }
  | { kind: 'checks_failing'; count: number }
  | { kind: 'changes_requested' }
  | { kind: 'needs_your_review' }
  | { kind: 'approved' }
  | { kind: 'checks_running' }
  | { kind: 'needs_review' }
  | { kind: 'waiting' };

export type PrV2StackStatusPresentation = {
  /** What the menu row says on its right; null when the PR says nothing. */
  label: { text: string; Icon: LucideIcon; iconClassName: string; className: string } | null;
  /** The line stays calm: its dots only tell merged, closed and draft PRs apart. */
  dotClassName: string;
  /** Fill of the PR's segment in the stack rail. */
  segmentClassName: string;
};

const OPEN_DOT_CLASS = 'bg-zinc-500';

const PR_V2_STACK_STATUS_PRESENTATION = {
  merged: {
    label: {
      text: 'Merged',
      Icon: GitMerge,
      iconClassName: 'text-purple-400',
      className: 'text-zinc-400',
    },
    dotClassName: 'bg-purple-400',
    segmentClassName: 'bg-purple-400',
  },
  closed: {
    label: {
      text: 'Closed',
      Icon: GitPullRequestClosed,
      iconClassName: 'text-destructive',
      className: 'text-zinc-400',
    },
    dotClassName: 'border border-destructive bg-black',
    segmentClassName: 'bg-destructive',
  },
  draft: {
    label: {
      text: 'Draft',
      Icon: CircleDashed,
      iconClassName: 'text-muted-foreground',
      className: 'text-zinc-500',
    },
    dotClassName: 'border border-dashed border-zinc-500 bg-black',
    segmentClassName: 'ring-1 ring-inset ring-zinc-600',
  },
  checks_failing: {
    label: {
      text: 'Checks failing',
      Icon: X,
      iconClassName: 'text-destructive',
      className: 'text-zinc-200',
    },
    dotClassName: OPEN_DOT_CLASS,
    segmentClassName: 'bg-destructive',
  },
  changes_requested: {
    label: {
      text: 'Changes requested',
      Icon: X,
      iconClassName: 'text-destructive',
      className: 'text-zinc-200',
    },
    dotClassName: OPEN_DOT_CLASS,
    segmentClassName: 'bg-destructive',
  },
  needs_your_review: {
    label: {
      text: 'Needs your review',
      Icon: Circle,
      iconClassName: 'text-yellow-600',
      className: 'text-zinc-200',
    },
    dotClassName: OPEN_DOT_CLASS,
    segmentClassName: 'bg-yellow-600',
  },
  approved: {
    label: {
      text: 'Approved',
      Icon: Check,
      iconClassName: 'text-green-700',
      className: 'text-zinc-400',
    },
    dotClassName: OPEN_DOT_CLASS,
    segmentClassName: 'bg-green-700',
  },
  checks_running: {
    label: {
      text: 'Checks running',
      Icon: Loader2,
      iconClassName: 'animate-spin text-muted-foreground',
      className: 'text-zinc-500',
    },
    dotClassName: OPEN_DOT_CLASS,
    segmentClassName: 'bg-zinc-600',
  },
  needs_review: {
    label: {
      text: 'Needs review',
      Icon: Circle,
      iconClassName: 'text-muted-foreground',
      className: 'text-zinc-500',
    },
    dotClassName: OPEN_DOT_CLASS,
    segmentClassName: 'bg-zinc-600',
  },
  waiting: { label: null, dotClassName: OPEN_DOT_CLASS, segmentClassName: 'bg-zinc-600' },
} satisfies Record<PrV2StackStatus['kind'], PrV2StackStatusPresentation>;

/**
 * First match wins. A PR that is only waiting for review says nothing, except the bottom open PR,
 * which is the one the stack waits on.
 */
export function getPrV2StackStatus(
  pullRequest: PrV2StackPullRequest,
  context: { viewerLogin: string | null; isBottomOpenPullRequest: boolean },
): PrV2StackStatus {
  const viewerLogin = context.viewerLogin?.toLowerCase();
  const failedChecks = pullRequest.checks?.failed ?? 0;

  if (pullRequest.merged) {
    return { kind: 'merged' };
  }
  if (pullRequest.state !== 'open') {
    return { kind: 'closed' };
  }
  if (pullRequest.draft) {
    return { kind: 'draft' };
  }
  if (failedChecks > 0) {
    return { kind: 'checks_failing', count: failedChecks };
  }
  if (pullRequest.reviewDecision === 'changes_requested') {
    return { kind: 'changes_requested' };
  }
  if (
    viewerLogin &&
    viewerLogin !== pullRequest.authorLogin?.toLowerCase() &&
    pullRequest.requestedReviewerLogins.some((login) => login.toLowerCase() === viewerLogin)
  ) {
    return { kind: 'needs_your_review' };
  }
  if (pullRequest.reviewDecision === 'approved') {
    return { kind: 'approved' };
  }
  if ((pullRequest.checks?.running ?? 0) > 0) {
    return { kind: 'checks_running' };
  }
  return context.isBottomOpenPullRequest ? { kind: 'needs_review' } : { kind: 'waiting' };
}

export function getPrV2StackStatusPresentation(
  status: PrV2StackStatus,
): PrV2StackStatusPresentation {
  if (status.kind !== 'checks_failing') {
    return PR_V2_STACK_STATUS_PRESENTATION[status.kind];
  }

  const presentation = PR_V2_STACK_STATUS_PRESENTATION.checks_failing;
  return {
    ...presentation,
    label: {
      ...presentation.label,
      text: `${status.count} ${status.count === 1 ? 'check' : 'checks'} failing`,
    },
  };
}
